import { alpha, styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import IconButton from '@mui/material/IconButton';
import Card from '@/components/Card';
import { Button } from '@/components/Button';

export const ToolsGrid = styled(Stack)`
  display: flex;
  gap: 20px;
`;

export const TopControlsRow = styled(Stack)({
  flexDirection: 'row',
  gap: '32px',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
});

export const ToolFilterGroup = styled(ToggleButtonGroup)`
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.grey['02']
        : theme.palette.primary.main};
  height: 42px;

  .MuiToggleButtonGroup-grouped {
    border: none;
    &:not(:first-of-type) {
      border-left: 1px solid
        ${({ theme }) =>
          theme.palette.mode === 'dark'
            ? theme.palette.transparent.tertiary
            : theme.palette.primary.main};
    }
  }
`;

export const ToolToggleButton = styled(ToggleButton)`
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.palette.primary.main};
  transition: all 0.25s ease;
  white-space: nowrap;
  display: flex;
  gap: 10px;
  align-content: center;

  @media (max-width: 600px) {
    padding: 6px 10px;
    font-size: 11px;
  }

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: 700;
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.12)};
  }

  &.Mui-selected:hover {
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.18)};
  }

  &:hover {
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.tertiary
        : theme.palette.transparent.quaternary};
  }
`;

export const ControlsRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
  gap: '16px',
});

export const ControlGroup = styled(Stack)({
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: '8px',
});

export const ControlLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey['01'],
  fontSize: '0.75rem',
  fontWeight: 500,
}));

export const InputWrapper = styled(Box)({
  position: 'relative',
  width: '280px',
});

export const CompactInputWrapper = styled(Box)({
  position: 'relative',
  width: '130px',
});

export const ErrorMessage = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  color: theme.palette.status.offline,
  fontSize: '11px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  zIndex: 2,
}));

export const ActionButton = styled(Button)({
  minWidth: '180px',
  height: '41px',
});

export const HistoryButton = styled(IconButton)`
  width: 42px;
  height: 42px;
  border-radius: 4px;
  color: ${({ theme }) => theme.palette.primary.main};
  border: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.grey['02']
        : theme.palette.primary.main};
  transition: all 0.25s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.tertiary
        : theme.palette.transparent.quaternary};
  }
`;

export const ViewPane = styled('div', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>(({ $active }) => ({
  display: $active ? 'flex' : 'none',
  flexDirection: 'column',
  gap: '20px',
  width: '100%',
}));

export const ToolContent = styled('div', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>(({ $active }) => ({
  display: $active ? 'block' : 'none',
  width: '100%',
}));

export const MainContainer = styled(Stack)({
  flexDirection: 'column',
  width: '100%',
});

export const CardsContainer = styled(Stack)({
  gap: '16px',
});

export const ToolHeaderRow = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '16px',
  marginBottom: '12px',
});

export const ToolTitleGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
});

export const ToolTitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.white['06'],
}));

export const ToolContentBox = styled(Box)({
  position: 'relative',
  marginTop: '8px',
  width: '100%',
});

export const StatusRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '20px',
});

export const HostTitleGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
});

export const HostNameText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.white['06'],
}));

export const MetricHeaderGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '6px',
});

export const MetricValueGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: '4px',
});

export const StatItemStart = styled(Stack)({
  alignItems: 'flex-start',
});

export const StatItemCenter = styled(Stack)({
  alignItems: 'center',
});

export const StatItemEnd = styled(Stack)({
  alignItems: 'flex-end',
});

export const StatItemNumberText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.white['06'],
}));

export const StatItemValueText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color?: string }>(({ $color }) => ({
  fontWeight: 700,
  color: $color,
}));

export const ConfigTitleGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
});

export const ConfigItemHeader = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
});

export const TwoCardsRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  alignItems: 'stretch',
  flexWrap: 'wrap',
  width: '100%',
});

export const CardColumn = styled(Box)({
  flex: '1 1 calc(50% - 8px)',
  minWidth: 280,
  display: 'flex',
});

export const FullHeightCard = styled(Card)({
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
});

export const CardContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

export const ConfigCardContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  gap: '16px',
});

export const PackagesRow = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

export const ProgressBarContainer = styled(Box)({
  width: '100%',
  marginTop: '16px',
});

export const LoadingOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(10, 15, 31, 0.75)'
      : 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  zIndex: 10,
  borderRadius: '24px',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(0, 207, 255, 0.2)'
      : 'rgba(37, 99, 235, 0.2)'
  }`,
  animation: 'fadeIn 0.3s ease-in-out',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
}));

export const LoadingText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.white['06'],
}));

export const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color?: string }>(({ theme, $color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  padding: '5px 12px',
  borderRadius: '6px',
  backgroundColor: $color || theme.palette.grey['01'],
  border: 'none',
  color: theme.palette.white['01'],
  boxShadow: 'none',
}));

export const StatusBadgeText = styled(Typography)(({ theme }) => ({
  color: theme.palette.white['01'],
  fontWeight: 600,
  lineHeight: 1,
  fontSize: '0.75rem',
}));

export const TimeMetricsGrid = styled('div')`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
`;

export const TimeMetricCard = styled(Box)(({ theme }) => ({
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(0, 207, 255, 0.07) 0%, rgba(28, 34, 48, 0.6) 100%)'
      : 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(243, 246, 251, 0.8) 100%)',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(0, 207, 255, 0.18)'
      : 'rgba(37, 99, 235, 0.14)'
  }`,
  borderRadius: '16px',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.2s ease-in-out',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 14px rgba(0, 0, 0, 0.25)'
      : '0 2px 8px rgba(37, 99, 235, 0.04)',
  '&:hover': {
    borderColor:
      theme.palette.mode === 'dark'
        ? 'rgba(0, 207, 255, 0.4)'
        : 'rgba(37, 99, 235, 0.35)',
    transform: 'translateY(-2px)',
  },
}));

export const ConfigGrid = styled('div')`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  flex: 1;
  height: 100%;
  width: 100%;
`;

export const ConfigItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '4px',
  padding: '12px 14px',
  borderRadius: '14px',
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(0, 207, 255, 0.06) 0%, rgba(28, 34, 48, 0.5) 100%)'
      : 'linear-gradient(135deg, rgba(37, 99, 235, 0.04) 0%, rgba(243, 246, 251, 0.7) 100%)',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(0, 207, 255, 0.15)'
      : 'rgba(37, 99, 235, 0.12)'
  }`,
  overflow: 'hidden',
  height: '100%',
  boxSizing: 'border-box',
  transition: 'all 0.2s ease-in-out',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 2px 8px rgba(0, 0, 0, 0.2)'
      : '0 2px 6px rgba(37, 99, 235, 0.03)',
  '&:hover': {
    borderColor:
      theme.palette.mode === 'dark'
        ? 'rgba(0, 207, 255, 0.35)'
        : 'rgba(37, 99, 235, 0.3)',
  },
}));

export const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '48px 24px',
  borderRadius: '24px',
  background:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(145deg, rgba(42, 52, 77, 0.85) 0%, rgba(0, 207, 255, 0.12) 100%)'
      : 'rgba(255, 255, 255, 0.6)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(255, 255, 255, 0.4)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.5)'
      : '0 4px 12px rgba(0, 0, 0, 0.04)',
}));

export const EmptyStateIconBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  borderRadius: '50%',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 207, 255, 0.1)'
      : 'rgba(37, 99, 235, 0.08)',
}));

export const EmptyStateText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey['01'],
  fontWeight: 500,
}));

export const HeaderIconBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px',
  borderRadius: '10px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 207, 255, 0.12)'
      : 'rgba(37, 99, 235, 0.08)',
  color: theme.palette.primary.main,
}));

export const ConfigHeaderLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontSize: '0.8rem',
}));

export const ConfigValueText = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.palette.white['06'],
  marginTop: '4px',
  fontWeight: 600,
  fontSize: '0.9rem',
}));

export const MetricLabelText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey['01'],
  fontSize: '0.75rem',
  fontWeight: 500,
}));

export const MetricValueText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color?: string }>(({ $color }) => ({
  color: $color,
  fontWeight: 700,
}));

export const MetricUnitText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey['01'],
  fontWeight: 500,
  fontSize: '0.75rem',
}));

export const StatLabelText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey['01'],
  fontSize: '0.75rem',
  fontWeight: 500,
}));

export const SectionDivider = styled(Divider)(({ theme }) => ({
  margin: '20px 0',
  borderColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)',
}));
